// CloudKeeper Org Services Portal — StackSets API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
import { portalFetch } from './portalApi';

// --- service config ---
export const getStackSetsConfig = () => portalFetch('/api/v1/stacksets/config');

// accountIds: accounts whose execution role is pre-provisioned now. Empty is
// valid — every other account gets its role at its first deploy.
export const enableStackSets = (adminAccountId, accountIds = []) =>
  portalFetch('/api/v1/stacksets/config', {
    method: 'POST',
    body: JSON.stringify({ adminAccountId, accountIds }),
  });

export const disableStackSets = (deleteTemplateBucket) =>
  portalFetch('/api/v1/stacksets/config', {
    method: 'DELETE',
    body: JSON.stringify({ deleteTemplateBucket: !!deleteTemplateBucket }),
  });

export const listStackSetsAccounts = () => portalFetch('/api/v1/stacksets/accounts');

// ONE level of the org tree. Omit parentId for the initial load, which returns
// the tenant root as `parent` plus its first level as `children`. The backend
// expands OU targets with this same call, one level per request: a whole-tree
// fetch in one request times out at the gateway on real orgs.
export const getStackSetsOrgChildren = (parentId) =>
  portalFetch('/api/v1/stacksets/org-children', {
    method: 'POST',
    body: JSON.stringify(parentId ? { parentId } : {}),
  });

export const listRoleStatuses = () => portalFetch('/api/v1/stacksets/roles');

export const retryRole = (accountId) =>
  portalFetch('/api/v1/stacksets/roles/retry', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });

// validateTemplate validates a pasted/uploaded body OR a customer-owned S3 URL
// (pass one or the other). The response echoes the normalized https URL.
export const validateTemplate = (templateBody, templateS3Url = '') =>
  portalFetch('/api/v1/stacksets/validate-template', {
    method: 'POST',
    body: JSON.stringify({ templateBody, templateS3Url }),
  });

// --- stacksets ---
export const listStackSets = () => portalFetch('/api/v1/stacksets/stacksets');

export const createStackSet = (body) =>
  portalFetch('/api/v1/stacksets/stacksets', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getStackSet = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}`);

export const updateStackSet = (id, body) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const deleteStackSet = (id, retainStacks) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({ retainStacks: !!retainStacks }),
  });

export const getStackSetTemplate = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/template`);

export const listInstances = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances`);

export const retryInstances = (id, accounts = [], regions = []) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances/retry`, {
    method: 'POST',
    body: JSON.stringify({ accounts, regions }),
  });

// listInstanceEvents returns the member-account CloudFormation stack's
// resource-level events for one instance (the AWS "Events" tab). `source` is
// optional: 'live' (the server default), 'snapshot' (events copied to the DB
// when an operation finished) or 'auto' (live, falling back to the snapshot).
export const listInstanceEvents = (id, account, region, source) =>
  portalFetch(
    `/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances/${encodeURIComponent(account)}/${encodeURIComponent(region)}/events${source ? `?source=${encodeURIComponent(source)}` : ''}`,
  );

// Both forms answer with a job ({ id, kind, status, error, syncPlan }) because
// resolving OU targets can outlive one request. dryRun: a "sync_preview" job —
// 200 already finished, or 202 running (poll getSyncPreview). A real Sync: 202
// with a "sync" job; its progress is on the StackSet itself.
export const syncStackSet = (id, { dryRun = false, retainStacks = false } = {}) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/sync`, {
    method: 'POST',
    body: JSON.stringify({ dryRun, retainStacks }),
  });

// The latest Sync preview job; job.syncPlan is set once job.status is 'succeeded'.
export const getSyncPreview = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/sync/preview`);

export const listOperations = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/operations`);

export const stopOperation = (id, opId) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/operations/${encodeURIComponent(opId)}/stop`, {
    method: 'POST',
  });

// getOperationResults returns the persisted per-cell (account, region) results
// of one operation.
export const getOperationResults = (id, opId) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/operations/${encodeURIComponent(opId)}/results`);
// --- execution roles (roles page) ---
export const addRoleAccounts = (accountIds) =>
  portalFetch('/api/v1/stacksets/roles/accounts', {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });

// Deletes the role in AWS, then drops the row. Refused while a StackSet still
// has a live instance in the account.
export const removeRole = (accountId) =>
  portalFetch(`/api/v1/stacksets/roles/${encodeURIComponent(accountId)}/remove`, { method: 'POST' });

// Drops the row without touching AWS (accounts Prism cannot reach).
export const dismissRole = (accountId) =>
  portalFetch(`/api/v1/stacksets/roles/${encodeURIComponent(accountId)}/dismiss`, { method: 'POST' });

// --- incomplete disable (status disable_failed) ---
export const retryDisable = () =>
  portalFetch('/api/v1/stacksets/disable/retry', { method: 'POST' });

export const finishDisable = () =>
  portalFetch('/api/v1/stacksets/disable/finish', { method: 'POST' });
// --- exclusions ---

// deleteInstance deletes ONE stack instance and records an exclusion so Sync
// and automatic deployment never re-create it.
export const deleteInstance = (id, account, region, retainStacks) =>
  portalFetch(
    `/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances/${encodeURIComponent(account)}/${encodeURIComponent(region)}/delete`,
    { method: 'POST', body: JSON.stringify({ retainStacks: !!retainStacks }) },
  );

// removeExclusions re-includes excluded cells ([{accountId, region}], region
// '*' = every region). Deploys nothing; the response carries a `message` hint.
export const removeExclusions = (id, cells) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/exclusions/remove`, {
    method: 'POST',
    body: JSON.stringify({ cells }),
  });

// --- drift and health ---

// Starts DetectStackSetDrift. Phases are not changed; each instance's
// driftStatus is filled in when the operation finishes.
export const detectStackSetDrift = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/drift`, { method: 'POST' });

// Resources of one instance as checked by the latest drift detection:
// { operationId, checkedAt, resources: [{ logicalResourceId, resourceType,
// driftStatus, differences: [{ propertyPath, expectedValue, actualValue }] }] }.
export const getInstanceDrift = (id, account, region) =>
  portalFetch(
    `/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances/${encodeURIComponent(account)}/${encodeURIComponent(region)}/drift`,
  );

// Starts reconciling instance rows with AWS and checking each member stack and
// execution role. Answers 202 with a "refresh" job; poll getRefreshStatus until
// job.status is not 'running'. job.refresh is
// { inserted, removed, missing, roleMissing, checked, warnings, external }.
export const refreshStackSetStatus = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/refresh-status`, { method: 'POST' });

export const getRefreshStatus = (id) =>
  portalFetch(`/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/refresh-status`);

// Re-creates a missing member stack or re-provisions a missing execution role.
export const repairInstance = (id, account, region) =>
  portalFetch(
    `/api/v1/stacksets/stacksets/${encodeURIComponent(id)}/instances/${encodeURIComponent(account)}/${encodeURIComponent(region)}/repair`,
    { method: 'POST' },
  );
